import axios from "axios";

/**
 * Thin axios wrapper for the /admin/dr/* endpoints.
 *
 * Centralizes two concerns that the existing page code would otherwise
 * duplicate:
 *   1. Base URL resolution — `process.env.REACT_APP_API` returns a string
 *      that may or may not end in `/`. Normalize to exactly one.
 *   2. Auth header — every call needs `x-access-token` from localStorage.
 *      Keeping the legacy header name so Phase 0 auth stays unchanged.
 *
 * Callers get plain promises that resolve to `response.data`. Errors bubble
 * so page components can decide how to display them (toast, inline, etc.).
 */

function apiBase() {
  const raw = process.env.REACT_APP_API || "";
  return raw.endsWith("/") ? raw : raw + "/";
}

function authHeaders() {
  const token = localStorage.getItem("accessToken");
  return token ? { "x-access-token": token } : {};
}

function url(path) {
  // Strip leading slash so we don't double it when concatenated with base.
  const clean = path.startsWith("/") ? path.slice(1) : path;
  return apiBase() + clean;
}

async function get(path, params) {
  const res = await axios.get(url(path), { headers: authHeaders(), params });
  return res.data;
}

async function post(path, body) {
  const res = await axios.post(url(path), body ?? {}, { headers: authHeaders() });
  return res.data;
}

// CredentialExpiryAlert renders on 6 DR pages; without a cache each mount
// fires a GET /admin/dr/credentials/me. 60-second in-module TTL de-dupes
// rapid navigation between pages. Write paths (createCredential) bust the
// cache so newly-submitted rows surface immediately.
let myCredentialsCache = null;
let myCredentialsFetchedAt = 0;
const MY_CREDENTIALS_TTL_MS = 60 * 1000;

function invalidateMyCredentialsCache() {
  myCredentialsCache = null;
  myCredentialsFetchedAt = 0;
}

export const drApi = {
  // Read-heavy endpoints.
  overview: () => get("admin/dr/overview"),
  myCredentials: async () => {
    const now = Date.now();
    if (myCredentialsCache && now - myCredentialsFetchedAt < MY_CREDENTIALS_TTL_MS) {
      return myCredentialsCache;
    }
    const data = await get("admin/dr/credentials/me");
    myCredentialsCache = data;
    myCredentialsFetchedAt = now;
    return data;
  },
  approvalsQueue: (params) => get("admin/dr/approvals/queue", params),
  approvalDetail: (slaId) => get(`admin/dr/approvals/${slaId}`),
  suitabilityFailures: (params) => get("admin/dr/suitability/failures", params),
  audit: (params) => get("admin/dr/audit", params),
  deals: (params) => get("admin/dr/deals", params),

  // Write endpoints.
  createCredential: async (body) => {
    const data = await post("admin/dr/credentials", body);
    invalidateMyCredentialsCache();
    return data;
  },
  decide: (slaId, body) => post(`admin/dr/approvals/${slaId}/decide`, body),
  claim: (slaId, body) => post(`admin/dr/approvals/${slaId}/claim`, body),
  suitabilityFail: (investmentId, body) =>
    post(`admin/dr/suitability/${investmentId}/fail`, body),
  callScheduleOutcome: (id, body) =>
    post(`admin/dr/call-schedules/${id}/outcome`, body),

  // Platform Manager (AD) oversight. AD no longer decides KYC, classification
  // or suitability: it hands the item to a reviewer or escalates it.
  adReassign: (slaId, body) =>
    post(`admin/ad/compliance/approvals/${slaId}/reassign`, body),
  adEscalate: (slaId, body) =>
    post(`admin/ad/compliance/approvals/${slaId}/escalate`, body),

  // Compliance review (CCO / UDP decide-and-review; AD reads).
  decisions: (params) => get("admin/compliance/decisions", params),
  reviewers: () => get("admin/compliance/reviewers"),
  reviewDecision: (slaId, body) =>
    post(`admin/compliance/decisions/${slaId}/review`, body),
  reverseDecision: (slaId, body) =>
    post(`admin/compliance/decisions/${slaId}/reverse`, body),
  confirmRegistration: (credentialId, body) =>
    post(`admin/compliance/credentials/${credentialId}/confirm-registration`, body),

  // Direct suitability decision from the investment payment ledger.
  decideInvestmentSuitability: (investmentId, body) =>
    post(`admin/compliance/investments/${investmentId}/suitability/decide`, body),

  // Testing hook - not used by pages.
  _invalidateMyCredentialsCache: invalidateMyCredentialsCache,
};
